import { GuardianReclaimAction } from 'app/components/guardian';
import { GuardianBadge } from 'app/shopify/components/feedback/GuardianBadge';
import { useUser } from 'app/network/user';
import { LoanFeePaymentDetails } from 'app/components/loan-fee-payment-details';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'app/hooks/navigation';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { TutorialRef } from 'app/types/tutorial';
import { Pressable, View } from 'react-native';

import { useOneLoan } from 'app/network/loan';
import { type Loan, LoanStatus } from 'app/types/loan';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import type { LoanFeeModalsState } from 'app/screens/management/loan-fee';
import { isRequestIcon } from 'app/lib/is-request-icon';
import { isDonationListing } from 'app/lib/listing';
import { RequestImage } from 'app/components/request-image';
import LenderAcceptedLoan from 'app/components/lender-accepted-loan';
import { StatusBadgeContent, useListingRequestCardBadge } from 'app/components/listing-request-card';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCalendarPlus, IconInfoCircle, IconPlugConnected, IconSettings } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface BannerProps {
  isLender: boolean;
  channelURL?: string;
  borrowerCreditExhausted?: boolean;
  listing: Listing;
  linkedOffer?: Listing | null;
  acceptedLoan?: Loan['id'];
  loanFeeModalsState: LoanFeeModalsState;
  showDatesPicker: () => void;
  showLinkOfferButton?: boolean;
  onLinkOffer?: () => void;
}

const buildTitle = (t: ReturnType<typeof useTranslation>['t'], isLender: boolean, listing?: Listing) => {
  if (listing?.type === ListingType.request) {
    if (isLender) {
      return t('banner.memberNeed');
    }
    return t('banner.yourNeed');
  }

  if (isLender) {
    return t('banner.yourOffer');
  }
  return t('banner.memberOffer');
};

const getActiveLoanId = (acceptedLoan: Loan['id'] | undefined, isPending: boolean, status: LoanStatus | undefined) => {
  if (isPending) return undefined;
  if (status !== LoanStatus.Accepted && status !== LoanStatus.Returned) return undefined;
  return acceptedLoan;
};

const getBannerListingState = (listing: Listing) => {
  const isService = listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(listing);
  const isSell = listing.transactionMode === TransactionMode.sell;
  const isParticipations = Boolean(listing.maxParticipants && listing.maxParticipants > 0);
  const isListingAvailable =
    listing.status === 'available' || listing.status === 'expired' || listing.type === ListingType.offer;
  const tPrefix = isParticipations
    ? 'manageParticipations'
    : isSell
      ? 'manageSale'
      : isService
        ? 'manageService'
        : isDonation
          ? 'manageDonation'
          : 'manageLoan';
  return { isDonation, isListingAvailable, isParticipations, isSell, isService, tPrefix };
};

const getPrimaryButtonText = ({
  activeLoanId,
  isDonation,
  isLender,
  isListingAvailable,
  isSell,
  isService,
  listing,
  t,
  tPrefix,
}: ReturnType<typeof getBannerListingState> & {
  activeLoanId: Loan['id'] | undefined;
  isLender: boolean;
  listing: Listing;
  t: TFunction;
}) => {
  if (activeLoanId) return t(`banner.${tPrefix}.${isLender ? 'lender' : 'borrower'}`);
  if (!isListingAvailable) return t(`banner.unavailable.${isLender ? 'lender' : 'borrower'}.${listing.type}`);
  if (isSell) return t('banner.saleDate');
  if (isDonation) return t('banner.donationDate');
  return isService ? t('banner.oneDate') : t(`banner.twoDates${isLender ? 'Lender' : 'Borrower'}`);
};

const BannerListingImage = ({ listing }: { listing: Listing }) => {
  if (!listing.picture) return null;
  if (isRequestIcon(listing.picture)) {
    return <RequestImage scale={0.8} isFound={Boolean(listing.lender)} />;
  }
  return (
    <PCImage
      aspectRatio={1}
      borderWidth={1}
      height={40}
      source={{ uri: listing.picture }}
      style={{
        borderColor: listing.picture.includes('categorie')
          ? listing.categoryPrimary.colorLight
          : theme.colors['surface.inverse.tertiary-neutral'],
        borderRadius: theme.borderRadii['radius.sm'],
      }}
    />
  );
};

const BannerListingSummary = ({
  badgeBg,
  cardStatus,
  isLender,
  linkedOffer,
  listing,
  loanFee,
  loanFeeModalsState,
  onShowListing,
  t,
}: {
  badgeBg: string;
  cardStatus: Parameters<typeof StatusBadgeContent>[0]['cardStatus'];
  isLender: boolean;
  linkedOffer: Listing | null | undefined;
  listing: Listing;
  loanFee: number | null | undefined;
  loanFeeModalsState: LoanFeeModalsState;
  onShowListing: () => void;
  t: TFunction;
}) => (
  <Column gap="spacing.general.x2">
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: listing.status === 'deleted' }}
      disabled={listing.status === 'deleted'}
      onPress={onShowListing}
    >
      <Row style={{ alignItems: 'center', gap: theme.spacing['spacing.general.x2'] }}>
        <Column>
          <BannerListingImage {...{ listing }} />
        </Column>
        <Column flex={1} minWidth={0}>
          <Row alignItems="center" gap="spacing.general.x1">
            <Text fontSize={12} numberOfLines={1} style={{ color: theme.colors['text.tertiary'] }}>
              {listing.status === 'deleted' ? t('banner.listingDeleted') : buildTitle(t, isLender, listing)}
            </Text>
            {linkedOffer ? <IconPlugConnected color={theme.colors['icon.success']} width={16} /> : null}
          </Row>
          <Text ellipsizeMode="tail" fontSize={16} numberOfLines={1}>
            {listing.item.name}
          </Text>
        </Column>
        {listing.guardianId ? <GuardianBadge /> : null}
        {listing.lender ? (
          <Row
            flexShrink={1}
            style={{
              backgroundColor: badgeBg,
              paddingVertical: 4,
              paddingHorizontal: theme.spacing['spacing.general.x1'],
              borderRadius: theme.borderRadii['radius.sm'],
            }}
          >
            <StatusBadgeContent cardStatus={cardStatus} before={undefined} hideListingsSubtype />
          </Row>
        ) : null}
      </Row>
    </Pressable>
    {loanFee ? (
      <Pressable onPress={isLender ? undefined : () => loanFeeModalsState.setIsLoanFeeOtherModalOpen(true)}>
        <Row alignItems="center" gap="spacing.general.x0,5">
          <Text variant="bodyBase">
            {t('loanFee.title', {
              ns: 'one-listing',
              amount: t('x$', { ns: 'common', amount: loanFee.toFixed(loanFee % 1 === 0 ? 0 : 2) }),
            })}
          </Text>
          {!isLender ? (
            <View style={{ marginVertical: -4 }}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
            </View>
          ) : null}
        </Row>
      </Pressable>
    ) : null}
  </Column>
);

const BannerActions = ({
  linkAction,
  primaryAction,
  suggestDatesButtonRef,
  t,
}: {
  linkAction?: () => void;
  primaryAction: {
    availability: 'enabled' | 'disabled';
    creditExhausted: boolean;
    showCreditExhaustedHint: boolean;
    emphasis: 'bold' | 'regular';
    icon: 'calendar' | 'settings' | 'none';
    onPress: () => void;
    text: string;
    visibility: 'hidden' | 'visible';
  };
  suggestDatesButtonRef: React.RefObject<View | null> | undefined;
  t: TFunction;
}) => (
  <>
    <BannerPrimaryAction primaryAction={primaryAction} suggestDatesButtonRef={suggestDatesButtonRef} t={t} />
    {linkAction ? (
      <Column marginTop="spacing.general.x2">
        <Button
          alignSelf="stretch"
          onPress={linkAction}
          style={{
            borderRadius: theme.borderRadii['radius.lg'],
            backgroundColor: theme.colors.white,
            borderColor: theme.colors['border.secondary'],
          }}
          variant="secondary"
        >
          <Row alignItems="center" gap="spacing.general.x2" justifyContent="center" minWidth={0} width="100%">
            <IconPlugConnected width={25} />
            <Text fontWeight="bold">{t('linkOffer')}</Text>
          </Row>
        </Button>
      </Column>
    ) : null}
  </>
);

function BannerPrimaryAction({
  primaryAction,
  suggestDatesButtonRef,
  t,
}: Pick<Parameters<typeof BannerActions>[0], 'primaryAction' | 'suggestDatesButtonRef' | 't'>) {
  if (primaryAction.visibility !== 'visible') return null;
  return (
    <Column gap="spacing.general.x1" marginTop="spacing.general.x2">
      <Button
        innerRef={suggestDatesButtonRef}
        disabled={primaryAction.availability === 'disabled'}
        alignSelf="stretch"
        testID="chat-suggest-dates-button"
        onPress={primaryAction.onPress}
        style={{
          borderRadius: 20,
          backgroundColor: theme.colors[primaryAction.creditExhausted ? 'surface.disabled' : 'surface.default'],
          borderColor: theme.colors['surface.disabled'],
        }}
        variant="secondary"
      >
        <Row alignItems="center" gap="spacing.general.x2" justifyContent="center" minWidth={0} width="100%">
          {primaryAction.icon === 'settings' ? <IconSettings width={25} /> : null}
          {primaryAction.icon === 'calendar' ? (
            <IconCalendarPlus
              color={primaryAction.creditExhausted ? theme.colors['icon.disabled'] : undefined}
              width={25}
            />
          ) : null}
          <Text
            color={primaryAction.creditExhausted ? 'text.disabled' : undefined}
            flexShrink={1}
            fontWeight={primaryAction.emphasis === 'bold' ? '700' : undefined}
            numberOfLines={1}
          >
            {primaryAction.text}
          </Text>
        </Row>
      </Button>
      {primaryAction.showCreditExhaustedHint ? (
        <Text color="text.secondary" textAlign="center" variant="bodyCaption">
          {t('banner.borrowerCreditExhausted')}
        </Text>
      ) : null}
    </Column>
  );
}

function getBannerActionAppearance({
  activeLoanId,
  isListingAvailable,
  lenderCannotProposeDates,
  isParticipations,
  hasLender,
}: {
  activeLoanId: Loan['id'] | undefined;
  isListingAvailable: boolean;
  lenderCannotProposeDates: boolean;
  isParticipations: boolean;
  hasLender: boolean;
}): Omit<Parameters<typeof BannerActions>[0]['primaryAction'], 'onPress' | 'text'> {
  const showCalendarIcon = !activeLoanId && isListingAvailable;
  return {
    availability: (activeLoanId || isListingAvailable) && !lenderCannotProposeDates ? 'enabled' : 'disabled',
    creditExhausted: lenderCannotProposeDates,
    showCreditExhaustedHint: lenderCannotProposeDates && isListingAvailable,
    emphasis: isListingAvailable || activeLoanId ? 'bold' : 'regular',
    icon: activeLoanId ? 'settings' : showCalendarIcon ? 'calendar' : 'none',
    visibility: (!isParticipations && !hasLender) || activeLoanId ? 'visible' : 'hidden',
  };
}

function getBannerDateEligibility({
  listing,
  isDonation,
  acceptedLoan,
  hasAcceptedLoanData,
  isLender,
  borrowerCreditExhausted,
  activeLoanId,
}: Pick<BannerProps, 'listing' | 'acceptedLoan' | 'isLender' | 'borrowerCreditExhausted'> & {
  isDonation: boolean;
  hasAcceptedLoanData: boolean;
  activeLoanId: Loan['id'] | undefined;
}) {
  const isBorrowing =
    listing.transactionMode === TransactionMode.lend ||
    ((listing.transactionMode === null || listing.transactionMode === undefined) && !isDonation);
  const isAcceptedLoanUnresolved = Boolean(acceptedLoan && !hasAcceptedLoanData);
  const lenderCannotProposeDates =
    isLender && isBorrowing && borrowerCreditExhausted === true && !activeLoanId && !isAcceptedLoanUnresolved;
  return { isBorrowing, isAcceptedLoanUnresolved, lenderCannotProposeDates };
}

const Banner = function Banner({
  isLender,
  borrowerCreditExhausted,
  channelURL,
  listing,
  linkedOffer,
  acceptedLoan,
  loanFeeModalsState,
  showDatesPicker,
  showLinkOfferButton,
  onLinkOffer,
}: BannerProps) {
  const navigation = useNavigation();
  const authorize = useIsExpired();
  const { t } = useTranslation('one-channel');
  const { data: currentUser } = useUser();
  const isGuardianshipConversation = Boolean(listing.guardianId && listing.guardianChannelId === channelURL);

  const acceptedLoanQuery = useOneLoan(acceptedLoan);
  const activeLoanId = getActiveLoanId(acceptedLoan, acceptedLoanQuery.isPending, acceptedLoanQuery.data?.status);

  const showListing = () => {
    if (listing.status === 'deleted') return;
    navigateToListing(navigation, listing.id);
  };

  const { viewRef: suggestDatesButtonRef, continueTutorial } = useTutorialRef(TutorialRef.SuggestDatesButton);

  const listingState = getBannerListingState(listing);
  const { isDonation, isListingAvailable, isParticipations } = listingState;
  const { isBorrowing, isAcceptedLoanUnresolved, lenderCannotProposeDates } = getBannerDateEligibility({
    listing,
    isDonation,
    acceptedLoan,
    hasAcceptedLoanData: Boolean(acceptedLoanQuery.data),
    isLender,
    borrowerCreditExhausted,
    activeLoanId,
  });
  const primaryButtonText = getPrimaryButtonText({ ...listingState, activeLoanId, isLender, listing, t });

  const primaryButtonPress = async () => {
    if (isAcceptedLoanUnresolved || lenderCannotProposeDates) return;
    if (isParticipations) {
      navigateToListing(navigation, listing.id);
      return;
    }
    if (activeLoanId) {
      navigateToLoan(navigation, activeLoanId, listing.id);
      return;
    }
    const proposeDates = async () => {
      await continueTutorial();
      // Reminder eligibility is handled by the picker after it becomes visible.
      showDatesPicker();
    };
    if (!isLender && isBorrowing) {
      await authorize(proposeDates);
    } else {
      await proposeDates();
    }
  };

  const { cardStatus, badgeBg } = useListingRequestCardBadge({
    createdAt: listing.createdAt,
    start: listing.start,
    status: listing.status,
    subtype: listing.subtype,
    end: listing.end,
    hideListingsSubtype: true,
    hasLender: !!listing.lender,
    isDonation: listing.isDonation,
    transactionMode: listing.transactionMode,
  });

  const loanFee = acceptedLoanQuery.data?.fee || listing.loanFee;

  return (
    <Column
      p="spacing.general.x4"
      paddingBottom="spacing.general.x1"
      style={{
        backgroundColor: theme.colors['surface.tertiary-neutral'],
        borderBottomColor: theme.colors['surface.disabled'],
        borderBottomWidth: 1,
      }}
    >
      <BannerListingSummary
        {...{ badgeBg, cardStatus, isLender, linkedOffer, listing, loanFee, loanFeeModalsState, t }}
        onShowListing={showListing}
      />

      <LoanFeePaymentDetails
        document={acceptedLoanQuery.data ? acceptedLoanQuery.data.feeDocument : listing.loanFeeDocument}
        showPaymentMethod={false}
      />

      {listing.lender ? (
        <LenderAcceptedLoan
          style={{ marginTop: theme.spacing['spacing.general.x3'] }}
          lender={listing.lender}
          t={t}
          scale={0.85}
        />
      ) : null}

      {isGuardianshipConversation ? (
        currentUser?.id === listing.userId ? (
          <GuardianReclaimAction listing={listing} />
        ) : null
      ) : (
        <BannerActions
          linkAction={showLinkOfferButton && !linkedOffer ? onLinkOffer : undefined}
          primaryAction={{
            ...getBannerActionAppearance({
              activeLoanId,
              isListingAvailable,
              lenderCannotProposeDates,
              isParticipations,
              hasLender: Boolean(listing.lender),
            }),
            availability:
              (activeLoanId || isListingAvailable) && !lenderCannotProposeDates && !isAcceptedLoanUnresolved
                ? 'enabled'
                : 'disabled',
            onPress: () => void primaryButtonPress(),
            text: primaryButtonText,
          }}
          suggestDatesButtonRef={suggestDatesButtonRef}
          t={t}
        />
      )}
    </Column>
  );
};

export { Banner };
